/** Header toolbar: readiness indicator, sound toggle, keyboard shortcut toggle, exit button. */
import { useCallback } from 'react';
import { motion } from 'motion/react';
import { X, Keyboard } from 'lucide-react';

import { useSession } from '../SessionContext';
import { useShortcutOverlay } from './ShortcutOverlay';
import { ReadinessIndicator } from './ReadinessIndicator';

// ─── Exit button ─────────────────────────────────────────────────────────────

function ExitButton({ onExit }: { onExit?: () => void }) {
  if (!onExit) return null;

  return (
    <button
      type="button"
      onClick={onExit}
      className="flex items-center justify-center rounded-lg p-2 opacity-40 transition-opacity hover:opacity-80 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
      style={{ color: 'var(--f-fg)' }}
      aria-label="End session"
    >
      <X size={20} />
    </button>
  );
}

// ─── Sound toggle ────────────────────────────────────────────────────────────

function AnimatedSoundIcon({ enabled, size = 20 }: { enabled: boolean; size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {/* Speaker body — static */}
      <path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z" />

      {/* Large wave (outer) — disappears first when muting, appears last when unmuting */}
      <motion.path
        d="M19.364 18.364a9 9 0 0 0 0-12.728"
        initial={false}
        animate={enabled ? 'on' : 'off'}
        variants={{
          on: { opacity: 1, transition: { duration: 0.15, ease: 'easeOut', delay: 0.35 } },
          off: { opacity: 0, transition: { duration: 0.15, ease: 'easeOut', delay: 0 } },
        }}
      />
      {/* Small wave (inner) — disappears after large when muting, appears first when unmuting */}
      <motion.path
        d="M16 9a5 5 0 0 1 0 6"
        initial={false}
        animate={enabled ? 'on' : 'off'}
        variants={{
          on: { opacity: 1, transition: { duration: 0.15, ease: 'easeOut', delay: 0.25 } },
          off: { opacity: 0, transition: { duration: 0.15, ease: 'easeOut', delay: 0.12 } },
        }}
      />

      {/* X strokes — draw in after waves disappear */}
      <motion.line
        x1="22"
        x2="16"
        y1="9"
        y2="15"
        initial={false}
        animate={{ pathLength: enabled ? 0 : 1, opacity: enabled ? 0 : 1 }}
        transition={{ duration: 0.2, ease: 'easeInOut', delay: enabled ? 0 : 0.3 }}
      />
      <motion.line
        x1="16"
        x2="22"
        y1="9"
        y2="15"
        initial={false}
        animate={{ pathLength: enabled ? 0 : 1, opacity: enabled ? 0 : 1 }}
        transition={{ duration: 0.2, ease: 'easeInOut', delay: enabled ? 0 : 0.36 }}
      />
    </svg>
  );
}

function SoundToggle() {
  const { state, dispatch } = useSession();
  const { soundEnabled } = state;

  const handleToggle = useCallback(() => {
    dispatch({ type: 'TOGGLE_SOUND' });
  }, [dispatch]);

  return (
    <button
      type="button"
      onClick={handleToggle}
      className="flex items-center justify-center rounded-lg p-2 opacity-40 transition-opacity hover:opacity-80 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
      style={{ color: 'var(--f-fg)' }}
      aria-label={soundEnabled ? 'Mute sound' : 'Unmute sound'}
    >
      <AnimatedSoundIcon enabled={soundEnabled} size={20} />
    </button>
  );
}

// ─── Shortcut toggle ──────────────────────────────────────────────────────────

function ShortcutToggle() {
  const { isPinned, togglePin } = useShortcutOverlay();

  return (
    <button
      type="button"
      onClick={togglePin}
      className={`hidden sm:flex items-center justify-center rounded-lg p-2 transition-opacity hover:opacity-80 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${isPinned ? 'opacity-80' : 'opacity-40'}`}
      style={{ color: 'var(--f-fg)' }}
      aria-label={isPinned ? 'Hide keyboard shortcuts' : 'Show keyboard shortcuts'}
      aria-pressed={isPinned}
    >
      <Keyboard size={20} />
    </button>
  );
}

// ─── Toolbar (shortcut + sound + exit, sits in header area) ──────────────────

export function SessionToolbar({ onExit }: { onExit?: () => void }) {
  return (
    <div className="flex flex-row items-center gap-1">
      <ReadinessIndicator size={20} />
      <ShortcutToggle />
      <SoundToggle />
      <ExitButton onExit={onExit} />
    </div>
  );
}
